import { assertNever } from '@dadohr/shared/domainTypes/00-assertNever';
import { Condition, MatchValue, Operator } from '@dadohr/shared/domainTypes/rules';
import styled from '@emotion/styled';
import {
  IconButton,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { sortBy } from 'lodash';
import { XCircle } from 'lucide-react';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteSingleSimpleKeyed } from '../../../../components/Autocomplete';
import { AutocompleteUserMultiById } from '../../../../components/AutocompleteUser';
import { MultiChipInput } from '../../../../components/MultiChipInput';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_VALUE_BY_DATA_TYPE,
  isBlankOperator,
  OPERATORS_BY_DATA_TYPE,
  OPERATORS_TO_STRING,
  usesSetOperators
} from './constants';
import {
  resolveConditionField, toValueList } from './utils';
import { RuleBuilderConfig, RuleBuilderConfigItem, RuleDataType } from './types';

const CondRowNormal = styled('div')<{root?: boolean}>`
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr 1fr 1fr min-content;
  grid-gap: ${(p) => p.theme.spacing(2)}px;
  padding: ${(p) => p.theme.spacing(2)}px;
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  background: ${(p) => p.theme.custom.colors.gray.w100};

  & > :last-child {
    display: flex;
    align-items: center;
    height: 100%;
  }
`;

const CondRowCompact = styled('div')`
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr min-content;
  grid-gap: ${(p) => p.theme.spacing()}px;  
  margin-bottom: 0;
  
  & > :last-child {
    display: flex;
    align-items: center;
    height: 100%;
  }
`;

const CondRowCompactInner = styled('div')<{root?: boolean}>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${(p) => p.theme.spacing()}px;
  margin-bottom: 0;
  padding: 12px;
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  background: ${(p) => p.theme.custom.colors.gray.w100};
`;

const UnknownFieldNotice = styled('div')`
  align-self: center;
  font-size: 0.8125rem;
  color: ${(p) => p.theme.palette.error.main};
`;

const CondTypeSelector = <T extends string>({
  value,
  onChange,
  config
}: {
  value: T;
  onChange: (nextValue: T) => void;
  config: RuleBuilderConfig<T>;
}) => {
  const { t } = useTranslation();
  const opts: RuleBuilderConfigItem<T>[] = useMemo(
    () => sortBy(Object.values(config), (o) => o.label),
    [config]
  );

  return (
    <AutocompleteSingleSimpleKeyed
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={t('Select a field')}
      toSelectKey={(o) => o.key}
      toLabel={(o) => o.label}
      fullWidth
    />
  );
};

const OperatorSelector = ({
  value,
  onChange,
  operators,
  type
}: {
  value: Operator;
  onChange: (nextValue: Operator) => void;
  operators: Operator[];
  type: RuleDataType;
}) => {
  const { t } = useTranslation();
  return (
    <AutocompleteSingleSimpleKeyed
      value={value}
      onChange={onChange}
      options={operators}
      toSelectKey={(o) => o}
      toLabel={(o) => t(OPERATORS_TO_STRING[type][o])}
      fullWidth
    />
  );
};

const CondValue = <T extends string>({
  value,
  config,
  operator,
  onChange
}: {
  config: RuleBuilderConfigItem<T>;
  value: MatchValue;
  operator?: Operator;
  onChange: (nextValue: MatchValue) => void;
}) => {
  const { t } = useUnsafeTranslation();
  
  if (config.dataType === 'boolean') {
    return (
      <Select
        value={value ? 'true' : 'false'}
        onChange={(ev) => onChange(ev.target.value === 'true')}
        variant="outlined"
      >
        <MenuItem value={'true'}>true</MenuItem>
        <MenuItem value={'false'}>false</MenuItem>
      </Select>
    );
  }

  if (config.dataType === 'user') {
    const currentIds = toValueList(value);
    return (
      <AutocompleteUserMultiById
        label=""
        orgId={config.options.orgId}
        value={currentIds}
        onChange={(nextIds: string[]) => onChange(nextIds as unknown as MatchValue)}
        fullWidth
      />
    );
  }

  if (config.dataType === 'string') {
    // For string fields, always use MultiChipInput
    const currentValues = Array.isArray(value) ? value : (value ? [value as string] : []);
    
    // Determine if blank option should be available based on operator
    // Only show blank option for isAnyOf and isNoneOf, and only when operator is defined
    const allowBlankOption = operator && ['isAnyOf', 'isNoneOf'].includes(operator);
    
    return (
      <MultiChipInput
        value={currentValues}
        onChange={(newValues: string[]) => {
          // Always store as array for consistency with new operators
          onChange(newValues);
        }}
        options={config.options?.autocomplete?.getItems || []}
        variant="outlined"
        fullWidth
        allowBlankOption={allowBlankOption}
      />
    );
  }
  
  if (config.dataType === 'number') {
    return (
      <TextField
        type="number"
        value={value}
        onChange={(ev) => onChange(parseInt(ev.target.value, 10) || 0)}
        inputProps={{ style: { height: 'auto' } }}
        variant="outlined"
        autoFocus={!value}
      />
    );
  }

  if (config.dataType === 'date') {
    return (
      <DatePicker
        variant="inline"
        inputVariant="outlined"
        disableToolbar
        fullWidth
        value={moment(value as number).tz(config.options.tz)}
        clearable={false}
        onChange={(d) => {
          d && onChange(d.startOf('d').valueOf());
        }}
        format={config.options.dateFormat ?? DEFAULT_DATE_FORMAT}
      />
    );
  }

  return assertNever(config.dataType);
};

type ConditionProps<T extends string> = {
  value: Condition<T>;
  onChange: (nextValue: Condition<T> | null) => void;
  config: RuleBuilderConfig<T>;
  variant: 'compact' | 'normal';
  canRemove?: boolean;
  root?: boolean;
};

export const Cond = <T extends string>({
  value,
  onChange,
  config,
  variant,
  canRemove = true,
  root
}: ConditionProps<T>) => {
  const { k, v, op } = value;
  const { c, showOperatorField, showValueField, operators, isUnknownField } = useMemo(
    () => resolveConditionField(value, config),
    [value, config]
  );

  const condTypeSelector = (
    <CondTypeSelector
      value={(isUnknownField ? '' : k) as T}
      config={config}
      onChange={(nextK) => {
        const nextConfig = config[nextK];
        const nextV = usesSetOperators(nextConfig.dataType) ? [] : DEFAULT_VALUE_BY_DATA_TYPE[nextConfig.dataType];
        const nextOperators =
          nextConfig.options?.allowedOperators ||
          OPERATORS_BY_DATA_TYPE[nextConfig.dataType];
        const nextOp = nextOperators.indexOf(op) !== -1 ? op : nextOperators[0];

        return onChange({
          k: nextK,
          // A blank operator ignores its value; '' is the shape the backend accepts.
          v: isBlankOperator(nextOp) ? '' : nextV,
          op: nextOp as Operator
        });
      }}
    />
  );
  
  const opSelector = showOperatorField ? (
    <OperatorSelector
      value={op}
      onChange={(nextOp) => {
        // When operator changes, reset value to appropriate default
        let nextV = v;
        if (usesSetOperators(c!.dataType)) {
          nextV = Array.isArray(v) && v.length > 0 ? v : [];
        }
        if (isBlankOperator(nextOp)) {
          // Drop any previously picked values; the backend rejects an array here.
          nextV = '';
        }

        onChange({
          ...value,
          op: nextOp,
          v: nextV
        });
      }}
      type={c!.dataType}
      operators={operators}
    />
  ) : (
    <div />
  );
  
  const condValue = showValueField ? (
    <CondValue
      value={v}
      key={k}
      operator={op}
      onChange={(nextV) => {
        onChange({
          ...value,
          v: nextV
        });
      }}
      config={c!}
    />
  ) : (
    <div />
  );
  
  const removeButton = canRemove ? (
    <FlexContainer>
      <IconButton onClick={() => onChange(null)}>
        <XCircle size={20} />
      </IconButton>
    </FlexContainer>
  ) : (
    <div />
  );

  const unknownFieldNotice = isUnknownField ? (
    <UnknownFieldNotice role="alert">{`Unknown field "${k}" — choose a field or remove this condition`}</UnknownFieldNotice>
  ) : null;

  if (variant === 'normal') {
    return (
      <CondRowNormal root={root}>
        {condTypeSelector}
        {unknownFieldNotice || opSelector}
        {condValue || <div />}
        {removeButton || <div />}
      </CondRowNormal>
    );
  }

  return (
    <CondRowCompact>
      <div>
        <CondRowCompactInner root={root}>
          {condTypeSelector}
          {unknownFieldNotice || opSelector}
          {condValue}
        </CondRowCompactInner>
      </div>
      {removeButton || <div />}
    </CondRowCompact>
  );
};